import { all, put, takeLatest, call, delay } from 'redux-saga/effects';
import { AuthorisationActionTypes, domainsResponse } from 'types';
import {
  listAuthorisationsFailure,
  listAuthorisationsSuccess,
  getAuthorisationStart,
  getAuthorisationSuccess,
  getAuthorisationFailure,
  addAuthorisationSuccess,
  addAuthorisationFailure,
  addAuthorisationOrderSuccess,
  addAuthorisationOrderFailure,
  addAuthorisationOrderStart,
  purchaseAuthorisationStart,
  purchaseAuthorisationSuccess,
  purchaseAuthorisationFailure,
  purchaseAuthorisationOrderStart,
  purchaseAuthorisationOrderSuccess,
  purchaseAuthorisationOrderFailure,
  deleteAuthorisationOrderStart,
  deleteAuthorisationOrderSuccess,
  deleteAuthorisationOrderFailure,
  deleteAuthorisationStart,
  deleteAuthorisationSuccess,
  deleteAuthorisationFailure,
  updateAuthorisationStart,
  updateAuthorisationSuccess,
  updateAuthorisationFailure,
  getAuthorisationTypesSuccess,
  getAuthorisationTypesFailure
} from './actions';
import {
  fetchAuthorisations,
  fetchAuthorisationDetails,
  fetchAuthorisationTypes,
  addAuthorisation,
  addAuthorisationOrder,
  purchaseAuthorisation,
  purchaseAuthorisationOrder,
  deleteAuthorisationOrder,
  deleteAuthorisation,
  updateAuthorisation
} from 'services/authorisation.service';
type AuthorisationListResponse = {
  Authorisations?: any[];
  Info?: {
    offset: number;
    limit: number;
    count: number;
  };
  error?: any;
};
function* handleListAuthorisations(request: any) {
  try {
    const response: AuthorisationListResponse = yield fetchAuthorisations(
      request.payload
    );
    if (response?.error) {
      yield put(listAuthorisationsFailure(response.error.message));
      return;
    }
    if (response?.Authorisations) {
      const authorisations = response.Authorisations.map((a) => {
        const {
          id,
          name,
          authorisationType,
          deletedAt,
          createdAt,
          channel,
          primaryContactName,
          primaryContactEmail,
          note,
          expires,
          transactionsLeft,
          externalId
        } = a;
        const users = a?.Users?.map((u: any) => u.email) || [];
        const domains = a?.Domains?.map((d: any) => d.domain) || [];
        const orders = a?.Orders || [];
        return {
          id,
          name,
          authorisationType,
          deletedAt,
          createdAt,
          users,
          domains,
          orders,
          channel,
          primaryContactName,
          primaryContactEmail,
          note,
          expires,
          transactionsLeft,
          externalId
        };
      });
      const totalAuthorisations = response.Info?.count;
      yield put(
        listAuthorisationsSuccess({ authorisations, totalAuthorisations })
      );
    } else {
      yield put(listAuthorisationsFailure('Unable to get authorisations'));
    }
  } catch (err) {
    yield put(listAuthorisationsFailure('Unable to get authorisations'));
  }
}
function* watchListAuthorisations() {
  yield takeLatest(
    AuthorisationActionTypes.LIST_AUTHORISATIONS_START,
    handleListAuthorisations
  );
}
function* handleGetAuthorisationDetails(
  action: ReturnType<typeof getAuthorisationStart>
) {
  try {
    const response = yield call(fetchAuthorisationDetails, action.payload);
    if (response.error) {
      yield put(getAuthorisationFailure(response.error.message));
    } else {
      const {
        id,
        name,
        authorisationType,
        channel,
        note,
        primaryContactEmail,
        primaryContactName,
        deletedAt,
        createdAt,
        externalId
      } = response;
      const users = response?.Users?.map((u: any) => u.email) || [];
      const domains = response?.Domains?.map((d: any) => d.domain) || [];
      const orders = response?.Orders || [];
      const ownerEmail = response?.Owner?.email || '';
      const transformedResponse = {
        id,
        name,
        authorisationType,
        channel,
        note,
        primaryContactEmail,
        primaryContactName,
        deletedAt,
        createdAt,
        ownerEmail,
        users,
        domains,
        orders,
        externalId
      };
      yield put(getAuthorisationSuccess(transformedResponse));
    }
  } catch (err) {
    yield put(getAuthorisationFailure('Unable to get authorisations'));
  }
}
function* watchGetAuthorisationDetails() {
  yield takeLatest(
    AuthorisationActionTypes.GET_AUTHORISATION_START,
    handleGetAuthorisationDetails
  );
}
function* addAuthorisationSaga(action: any) {
  try {
    const response = yield call(addAuthorisation, action.payload);
    if (response.error) {
      if (response.error.message === 'USERS_NOT_FOUND') {
        yield put(addAuthorisationFailure('Users not found'));
      } else {
        yield put(
          addAuthorisationFailure(
            response.error.message || 'Unable to add authorisations'
          )
        );
      }
    } else {
      yield put(addAuthorisationSuccess(response));
    }
  } catch (error) {
    yield put(addAuthorisationFailure('Unable to add authorisations'));
  }
}
export function* watchAddAuthorisation() {
  yield takeLatest(
    AuthorisationActionTypes.ADD_AUTHORISATION_START,
    addAuthorisationSaga
  );
}
function* addOrderSaga(action: ReturnType<typeof addAuthorisationOrderStart>) {
  try {
    const { id, payload } = action.payload;
    const order = yield call(addAuthorisationOrder, id, payload);
    yield put(addAuthorisationOrderSuccess(order));
    yield delay(100);
    yield put(getAuthorisationStart(id));
  } catch (error) {
    yield put(
      addAuthorisationOrderFailure('Unable to add authorisation order')
    );
  }
}
export function* watchAddOrderSaga() {
  yield takeLatest(
    AuthorisationActionTypes.ADD_AUTHORISATION_ORDER_START,
    addOrderSaga
  );
}
function* deleteOrderSaga(
  action: ReturnType<typeof deleteAuthorisationOrderStart>
) {
  try {
    const { authorisationId, orderId } = action.payload;
    yield call(deleteAuthorisationOrder, authorisationId, orderId);
    yield put(deleteAuthorisationOrderSuccess({ orderId }));
    yield delay(100);
    yield put(getAuthorisationStart(authorisationId));
  } catch (error) {
    yield put(
      deleteAuthorisationOrderFailure('Unable to delete authorisation order')
    );
  }
}

export function* watchDeleteOrderSaga() {
  yield takeLatest(
    AuthorisationActionTypes.DELETE_AUTHORISATION_ORDER_START,
    deleteOrderSaga
  );
}
function* deleteAuthorisationSaga(
  action: ReturnType<typeof deleteAuthorisationStart>
) {
  try {
    yield call(deleteAuthorisation, action.payload);
    yield put(deleteAuthorisationSuccess(action.payload));
  } catch (error) {
    yield put(deleteAuthorisationFailure('Unable to delete authorisation'));
  }
}
export function* watchAuthorisationSagas() {
  yield takeLatest(
    AuthorisationActionTypes.DELETE_AUTHORISATION_START,
    deleteAuthorisationSaga
  );
}
function* updateAuthorisationSaga(
  action: ReturnType<typeof updateAuthorisationStart>
) {
  try {
    const { id, payload } = action.payload;
    const response = yield call(updateAuthorisation, id, payload);

    if (response.error) {
      if (response.error.message === 'USERS_NOT_FOUND') {
        yield put(updateAuthorisationFailure('Unable to update authorisation'));
      } else {
        yield put(
          updateAuthorisationFailure(
            response.error.message || 'Unable to add authorisations'
          )
        );
      }
    } else {
      yield put(updateAuthorisationSuccess(response));
    }
  } catch (error) {
    yield put(updateAuthorisationFailure('Unable to update authorisation'));
  }
}
export function* watchUpdateAuthorisationSaga() {
  yield takeLatest(
    AuthorisationActionTypes.UPDATE_AUTHORISATION_START,
    updateAuthorisationSaga
  );
}

function* handleGetAuthorisationTypes() {
  try {
    const responses: domainsResponse[] = yield call(fetchAuthorisationTypes);
    if (Array.isArray(responses)) {
      const options = responses.map((r) => {
        const { label, code } = r;
        return { label, value: code };
      });
      yield put(getAuthorisationTypesSuccess(options));
    } else {
      yield put(getAuthorisationTypesFailure());
    }
  } catch (error) {
    yield put(getAuthorisationTypesFailure());
  }
}

function* watchGetAuthorisationTypes() {
  yield takeLatest(
    AuthorisationActionTypes.GET_AUTHORISATION_TYPES_START,
    handleGetAuthorisationTypes
  );
}

function* handlePurchaseAuthorisation(
  action: ReturnType<typeof purchaseAuthorisationStart>
) {
  try {
    const response = yield call(purchaseAuthorisation, action.payload);
    if (response.error) {
      if (response.error.message === 'USERS_NOT_FOUND') {
        yield put(
          purchaseAuthorisationFailure('One or more user emails were not found')
        );
      } else if (response.error.message === 'DUPLICATE_AUTHORISATION_DOMAINS') {
        yield put(
          purchaseAuthorisationFailure('One or more domains were taken')
        );
      } else if (response.error.message === 'DUPLICATE_AUTHORISATION_USERS') {
        yield put(
          purchaseAuthorisationFailure('One or more user emails were taken')
        );
      } else if (response.error.message === 'DUPLICATE_AUTHORISATION_USER_ID') {
        yield put(
          purchaseAuthorisationFailure('transaction type already exists')
        );
      } else {
        yield put(
          purchaseAuthorisationFailure(
            response.error.message || 'Unable to purchase new transactions'
          )
        );
      }
    } else {
      yield put(purchaseAuthorisationSuccess(response));
    }
  } catch (error) {
    yield put(purchaseAuthorisationFailure('Unable to purchase authorisation'));
  }
}

function* watchPurchaseAuthorisation() {
  yield takeLatest(
    AuthorisationActionTypes.PURCHASE_AUTHORISATION_START,
    handlePurchaseAuthorisation
  );
}

function* handlePurchaseAuthorisationOrder(
  action: ReturnType<typeof purchaseAuthorisationOrderStart>
) {
  try {
    const { id, payload } = action.payload;
    const response = yield call(purchaseAuthorisationOrder, id, payload);
    if (response.error) {
      if (response.error.message === 'USERS_NOT_FOUND') {
        yield put(
          purchaseAuthorisationOrderFailure(
            'One or more user emails were not found'
          )
        );
      } else if (response.error.message === 'DUPLICATE_AUTHORISATION_DOMAINS') {
        yield put(
          purchaseAuthorisationOrderFailure('One or more domains were taken')
        );
      } else if (response.error.message === 'DUPLICATE_AUTHORISATION_USERS') {
        yield put(
          purchaseAuthorisationOrderFailure(
            'One or more user emails were taken'
          )
        );
      } else if (response.error.message === 'DUPLICATE_AUTHORISATION_USER_ID') {
        yield put(
          purchaseAuthorisationOrderFailure('transaction type already exists')
        );
      } else {
        yield put(
          purchaseAuthorisationOrderFailure(
            response.error.message || 'Unable to top up order'
          )
        );
      }
    } else {
      yield put(purchaseAuthorisationOrderSuccess(response));
    }
  } catch (error) {
    yield put(
      purchaseAuthorisationOrderFailure(
        'Unable to purchase authorisation order'
      )
    );
  }
}

function* watchPurchaseAuthorisationOrder() {
  yield takeLatest(
    AuthorisationActionTypes.PURCHASE_AUTHORISATION_ORDER_START,
    handlePurchaseAuthorisationOrder
  );
}

function* AuthorisationsSagas() {
  yield all([
    call(watchListAuthorisations),
    call(watchGetAuthorisationDetails),
    call(watchAddAuthorisation),
    call(watchAddOrderSaga),
    call(watchPurchaseAuthorisation),
    call(watchPurchaseAuthorisationOrder),
    call(watchDeleteOrderSaga),
    call(watchAuthorisationSagas),
    call(watchUpdateAuthorisationSaga),
    call(watchGetAuthorisationTypes)
  ]);
}
export default AuthorisationsSagas;
